import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const features_free = [
  "Full parent assessment",
  "AI-generated personalized report",
  "Curriculum explorer (18+ curricula)",
  "Future Career Navigator",
  "Community discovery",
  "3 AI Assistant chats / day",
];
const features_premium = [
  "Everything in Free",
  "Unlimited AI Assistant chats",
  "Unlimited assessment updates",
  "AI Education Twin per child",
  "Personalized weekly plans",
  "Mentor & tutor recommendations",
  "Advanced progress analytics",
  "Portfolio builder & certificates",
  "Parent masterclass access",
];
const features_community = [
  "Bulk parent access",
  "Custom curriculum builder",
  "Admin dashboard",
  "White-label options",
  "Priority support",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const startCheckout = async (lookupKey) => {
    if (!user) {
      toast.info("Please sign in first to upgrade.");
      navigate("/login");
      return;
    }
    setLoading(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <Sparkles className="text-brand-orange mx-auto mb-3" />
        <h1 data-testid="pricing-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue">Simple, honest pricing.</h1>
        <p className="mt-4 text-lg text-brand-muted">Start free forever. Upgrade when you want the full ecosystem.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Free */}
        <div className="surface rounded-3xl p-8 flex flex-col">
          <div className="overline">For every parent</div>
          <h3 className="mt-2 text-2xl font-semibold font-heading text-brand-blue">Free</h3>
          <div className="mt-3 text-4xl font-heading font-bold">₹0</div>
          <ul className="mt-6 space-y-2.5 flex-1">
            {features_free.map((f) => (
              <li key={f} className="flex gap-2 text-sm"><Check size={16} className="text-brand-green shrink-0 mt-0.5" /><span className="text-brand-muted">{f}</span></li>
            ))}
          </ul>
          <Link to="/assessment" data-testid="pricing-cta-free" className="mt-8 btn-outline">Get started</Link>
        </div>

        {/* Premium — Monthly + Annual */}
        <div className="rounded-3xl p-8 flex flex-col bg-gradient-to-br from-brand-blue to-brand-blue-hover text-white shadow-[0_30px_80px_-30px_rgba(15,41,66,0.5)]">
          <div className="overline text-brand-orange">Most popular</div>
          <h3 className="mt-2 text-2xl font-semibold font-heading text-white">Premium</h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-4xl font-heading font-bold">₹499</span>
            <span className="text-white/70 text-sm">/ month</span>
          </div>
          <div className="text-xs text-white/70 mt-1">or ₹4,990 / year (save 2 months)</div>
          <ul className="mt-6 space-y-2.5 flex-1">
            {features_premium.map((f) => (
              <li key={f} className="flex gap-2 text-sm"><Check size={16} className="text-brand-orange shrink-0 mt-0.5" /><span className="text-white/90">{f}</span></li>
            ))}
          </ul>
          <div className="mt-6 space-y-2">
            <button
              data-testid="pricing-cta-premium-monthly"
              onClick={() => startCheckout("premium_monthly")}
              disabled={loading === "premium_monthly"}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading === "premium_monthly" ? (<><Loader2 className="animate-spin" size={14} /> Redirecting...</>) : (<>Start monthly — ₹499</>)}
            </button>
            <button
              data-testid="pricing-cta-premium-annual"
              onClick={() => startCheckout("premium_annual")}
              disabled={loading === "premium_annual"}
              className="btn-outline w-full text-white border-white/40 hover:border-white hover:bg-white/10 disabled:opacity-60"
            >
              {loading === "premium_annual" ? (<><Loader2 className="animate-spin" size={14} /> Redirecting...</>) : (<>Save with annual — ₹4,990</>)}
            </button>
          </div>
        </div>

        {/* Community */}
        <div className="surface rounded-3xl p-8 flex flex-col">
          <div className="overline">For communities & schools</div>
          <h3 className="mt-2 text-2xl font-semibold font-heading text-brand-blue">Community</h3>
          <div className="mt-3 text-4xl font-heading font-bold">Custom</div>
          <ul className="mt-6 space-y-2.5 flex-1">
            {features_community.map((f) => (
              <li key={f} className="flex gap-2 text-sm"><Check size={16} className="text-brand-green shrink-0 mt-0.5" /><span className="text-brand-muted">{f}</span></li>
            ))}
          </ul>
          <Link to="/contact" data-testid="pricing-cta-community" className="mt-8 btn-outline">Contact us</Link>
        </div>
      </div>

      <p className="mt-10 text-center text-xs text-brand-muted max-w-2xl mx-auto">
        Secure payments via Stripe. Test mode currently — use card <strong>4242 4242 4242 4242</strong>, any future expiry, any CVC.
      </p>
    </div>
  );
}
